import { parse } from 'date-fns';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { LogProject } from '../services/timeEntryService';
import { colors } from '../theme/colors';

interface LeistungsnachweisFiltersProps {
  logProjectsData: LogProject[];
  initialProject?: string;
  initialMonth?: string;
  initialEmployee?: string;
  onFilterChange: (project: string, month: string, employee: string) => void;
}

const GERMAN_MONTH_NAMES = [
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];

export const LeistungsnachweisFilters: React.FC<LeistungsnachweisFiltersProps> = ({
  logProjectsData,
  initialProject = '',
  initialMonth = '',
  initialEmployee = '',
  onFilterChange,
}) => {
  const [selectedProject, setSelectedProject] = useState<string>(initialProject);
  const [selectedMonth, setSelectedMonth] = useState<string>(initialMonth);
  const [selectedEmployee, setSelectedEmployee] = useState<string>(initialEmployee);

  useEffect(() => {
    setSelectedProject(initialProject);
    setSelectedMonth(initialMonth);
    setSelectedEmployee(initialEmployee);
  }, [initialProject, initialMonth, initialEmployee]);

  const parseDate = (dateStr: string): Date | null => {
    try {
      return parse(dateStr, 'dd.MM.yyyy', new Date());
    } catch {
      return null;
    }
  };

  const getMonthYearKey = (date: Date): string => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  const formatMonthYear = (monthYearKey: string): string => {
    const [year, month] = monthYearKey.split('-');
    const monthIndex = parseInt(month, 10) - 1;
    return `${GERMAN_MONTH_NAMES[monthIndex]} ${year}`;
  };

  const projects = useMemo(() => {
    const uniqueProjects = Array.from(new Set(logProjectsData.map((item) => item.project))).sort();
    return uniqueProjects;
  }, [logProjectsData]);

  const availableMonths = useMemo(() => {
    if (!selectedProject) return [];
    const filtered = logProjectsData.filter((item) => item.project === selectedProject);
    const monthSet = new Set<string>();
    filtered.forEach((item) => {
      const date = parseDate(item.date);
      if (date) {
        monthSet.add(getMonthYearKey(date));
      }
    });
    return Array.from(monthSet).sort();
  }, [logProjectsData, selectedProject]);

  const availableEmployees = useMemo(() => {
    if (!selectedProject || !selectedMonth) return [];
    const filtered = logProjectsData.filter((item) => {
      if (item.project !== selectedProject) return false;
      const date = parseDate(item.date);
      if (!date) return false;
      return getMonthYearKey(date) === selectedMonth;
    });
    const employeeSet = new Set<string>();
    filtered.forEach((item) => {
      employeeSet.add(`${item.employee_id}|${item.employee_name}`);
    });
    return Array.from(employeeSet)
      .map((emp) => {
        const [id, name] = emp.split('|');
        return { id, name };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [logProjectsData, selectedProject, selectedMonth]);

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newProject = e.target.value;
    setSelectedProject(newProject);
    setSelectedMonth('');
    setSelectedEmployee('');
    onFilterChange(newProject, '', '');
  };

  const handleMonthChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newMonth = e.target.value;
    setSelectedMonth(newMonth);
    setSelectedEmployee('');
    onFilterChange(selectedProject, newMonth, '');
  };

  const handleEmployeeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newEmployee = e.target.value;
    setSelectedEmployee(newEmployee);
    onFilterChange(selectedProject, selectedMonth, newEmployee);
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        alignItems: 'flex-end',
      }}
    >
      <div style={{ flex: '1', minWidth: '200px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: colors.secondary.text,
            marginBottom: '0.5rem',
          }}
        >
          Projekt
        </label>
        <select
          value={selectedProject}
          onChange={handleProjectChange}
          style={{
            width: '100%',
            padding: '0.625rem',
            border: `1px solid ${colors.neutral.grey[300]}`,
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            outline: 'none',
            transition: 'border-color 0.2s',
            backgroundColor: 'white',
          }}
          onFocus={(e) => (e.target.style.borderColor = colors.accent.blue)}
          onBlur={(e) => (e.target.style.borderColor = colors.neutral.grey[300])}
        >
          <option value="">Projekt auswählen</option>
          {projects.map((project) => (
            <option key={project} value={project}>
              {project}
            </option>
          ))}
        </select>
      </div>

      <div style={{ flex: '1', minWidth: '200px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: colors.secondary.text,
            marginBottom: '0.5rem',
          }}
        >
          Monat
        </label>
        <select
          value={selectedMonth}
          onChange={handleMonthChange}
          disabled={!selectedProject}
          style={{
            width: '100%',
            padding: '0.625rem',
            border: `1px solid ${colors.neutral.grey[300]}`,
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            outline: 'none',
            transition: 'border-color 0.2s',
            backgroundColor: selectedProject ? 'white' : colors.neutral.grey[100],
            cursor: selectedProject ? 'pointer' : 'not-allowed',
          }}
          onFocus={(e) => {
            if (selectedProject) e.target.style.borderColor = colors.accent.blue;
          }}
          onBlur={(e) => (e.target.style.borderColor = colors.neutral.grey[300])}
        >
          <option value="">Monat auswählen</option>
          {availableMonths.map((month) => (
            <option key={month} value={month}>
              {formatMonthYear(month)}
            </option>
          ))}
        </select>
      </div>

      <div style={{ flex: '1', minWidth: '200px' }}>
        <label
          style={{
            display: 'block',
            fontSize: '0.875rem',
            fontWeight: '500',
            color: colors.secondary.text,
            marginBottom: '0.5rem',
          }}
        >
          Mitarbeiter
        </label>
        <select
          value={selectedEmployee}
          onChange={handleEmployeeChange}
          disabled={!selectedProject || !selectedMonth}
          style={{
            width: '100%',
            padding: '0.625rem',
            border: `1px solid ${colors.neutral.grey[300]}`,
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            outline: 'none',
            transition: 'border-color 0.2s',
            backgroundColor: selectedProject && selectedMonth ? 'white' : colors.neutral.grey[100],
            cursor: selectedProject && selectedMonth ? 'pointer' : 'not-allowed',
          }}
          onFocus={(e) => {
            if (selectedProject && selectedMonth) e.target.style.borderColor = colors.accent.blue;
          }}
          onBlur={(e) => (e.target.style.borderColor = colors.neutral.grey[300])}
        >
          <option value="">Mitarbeiter auswählen</option>
          {availableEmployees.map((emp) => (
            <option key={emp.id} value={`${emp.id}|${emp.name}`}>
              {emp.name}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

